import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, concat, map, Observable, of, reduce, throwError } from 'rxjs';

import { BROUTER_MAP_CONFIG } from '../config/brouter-map.config';
import {
  BRouterSegment,
  ExportFormat,
  LatLng,
  Poi,
  RoutingOptions
} from '../models/brouter-map.model';
import { buildUrl, ParsedRouteState, parseUrlParams } from './brouter-url';
import { RoutingError } from './routing-error';

/** Content type the engine uses for a successful GeoJSON response. */
const GEOJSON_CONTENT_TYPE = 'application/vnd.geo+json';

/**
 * Talks to the BRouter engine.
 *
 * Replaces `L.BRouter` from the legacy client. The URL contract lives in `brouter-url.ts`
 * and is verified against fixtures captured from that client; this service only adds
 * transport, error handling and request sequencing.
 */
@Injectable({ providedIn: 'root' })
export class BrouterRouterService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(BROUTER_MAP_CONFIG);

  /** The profile that is implied when none is present in serialised route state. */
  get defaultProfile(): string {
    return this.config.profiles[0];
  }

  /**
   * Routes a single leg.
   *
   * The engine is queried one leg at a time, mirroring the legacy client, so that an
   * edit to one waypoint only re-routes the two legs that touch it.
   */
  getRouteSegment(from: LatLng, to: LatLng, options: RoutingOptions): Observable<BRouterSegment> {
    const url = buildUrl({
      host: this.config.host,
      defaultProfile: this.defaultProfile,
      latLngs: [from, to],
      options,
      format: 'geojson'
    });

    return this.http.get(url, { observe: 'response', responseType: 'text' }).pipe(
      map(response => {
        const contentType = (response.headers.get('Content-Type') ?? '').split(';')[0];
        if (contentType !== GEOJSON_CONTENT_TYPE) {
          // The engine reports routing failures as text/plain with a 200 status.
          throw new Error(response.body ?? 'Unknown routing error');
        }
        const geojson = JSON.parse(response.body ?? '') as {
          features: BRouterSegment[];
        };
        return geojson.features[0];
      }),
      catchError((error: unknown) => throwError(() => this.toRoutingError(error)))
    );
  }

  /**
   * Routes every leg of a track in order.
   *
   * Legs flagged as beelines are skipped, matching the legacy client, which draws them
   * as straight lines without consulting the engine. Requests are issued sequentially;
   * `switchMap` at the call site cancels any in-flight legs when the route changes again.
   */
  routeSegments(
    waypoints: readonly LatLng[],
    options: RoutingOptions,
    beelineFlags: readonly boolean[] = []
  ): Observable<(BRouterSegment | null)[]> {
    if (waypoints.length < 2) {
      return of([]);
    }

    const legs: Observable<BRouterSegment | null>[] = [];
    for (let i = 0; i < waypoints.length - 1; i++) {
      legs.push(
        beelineFlags[i] ? of(null) : this.getRouteSegment(waypoints[i], waypoints[i + 1], options)
      );
    }

    return concat(...legs).pipe(
      reduce(
        (acc: (BRouterSegment | null)[], segment) => [...acc, segment],
        [] as (BRouterSegment | null)[]
      )
    );
  }

  /** Builds a download URL for the export dialog. The engine renders the file itself. */
  getExportUrl(
    waypoints: readonly LatLng[],
    options: RoutingOptions,
    format: ExportFormat,
    trackname?: string,
    exportWaypoints = false,
    pois: readonly Poi[] = []
  ): string {
    return buildUrl({
      host: this.config.host,
      defaultProfile: this.defaultProfile,
      latLngs: waypoints,
      options,
      pois,
      format,
      trackname: trackname ? encodeURIComponent(trackname) : undefined,
      exportWaypoints
    });
  }

  /**
   * Serialises route state into query parameters.
   *
   * The legacy client kept this in `location.hash` so routes could be shared. Inside the
   * Angular app there is no user-visible URL, so it is used purely to persist state
   * across component teardown.
   */
  serialiseRouteState(
    waypoints: readonly LatLng[],
    options: RoutingOptions,
    beelineFlags: readonly boolean[] = [],
    pois: readonly Poi[] = []
  ): string {
    return buildUrl({
      host: this.config.host,
      defaultProfile: this.defaultProfile,
      latLngs: waypoints,
      options,
      beelineFlags,
      pois,
      format: null
    }).replace('/brouter?', '');
  }

  /** Inverse of {@link serialiseRouteState}. */
  deserialiseRouteState(query: string): ParsedRouteState {
    const params: Record<string, string> = {};
    for (const pair of query.split('&')) {
      const idx = pair.indexOf('=');
      if (idx > 0) {
        params[pair.slice(0, idx)] = decodeURIComponent(pair.slice(idx + 1));
      }
    }
    return parseUrlParams(params, this.config.profilesRename);
  }

  private toRoutingError(error: unknown): RoutingError {
    if (error instanceof RoutingError) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      const body = typeof error.error === 'string' ? error.error.trim() : '';
      // Proxies and web servers answer with HTML error pages, which say nothing useful.
      if (!body || body.startsWith('<')) {
        return new RoutingError(`${error.status} ${error.statusText}`.trim(), 'unreachable');
      }
      return new RoutingError(body);
    }

    return new RoutingError(error instanceof Error ? error.message : String(error));
  }
}
